import React, { useEffect, useRef, useState } from "react";
import api from "../services/api";
import Header from "../components/Header";
import SeloSystems from "../components/SeloSystems";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Field, Input, dataBR } from "../components/ui";
import { aplicarCorPrimaria } from "../lib/cores";
import {
    ESCALAS_DISPONIVEIS, aplicarEscalaInterface, lerEscalaInterface,
    TEMAS_DISPONIVEIS, aplicarTema, lerTema,
    DENSIDADES_DISPONIVEIS, aplicarDensidade, lerDensidade,
    FONTES_DISPONIVEIS, aplicarFonte, lerFonte,
    CONTRASTES_DISPONIVEIS, aplicarContraste, lerContraste
} from "../lib/preferencias";

// Tons que combinam com o papel bege — do dourado do selo a alternativas sóbrias
const CORES_SUGERIDAS = ["#b8862b", "#7a5c3e", "#1f6f5c", "#2455a4", "#7a3ba0", "#8c2418"];

const Configuracoes = () => {
    const { usuario, ehProprietario, logoEscritorio, recarregarEscritorio } = useAuth();
    const [cor, setCor] = useState(usuario?.cor_primaria || "#b8862b");
    const [escala, setEscala] = useState(lerEscalaInterface);
    const [tema, setTema] = useState(lerTema);
    const [densidade, setDensidade] = useState(lerDensidade);
    const [fonte, setFonte] = useState(lerFonte);
    const [contraste, setContraste] = useState(lerContraste);
    const [nomeEscritorio, setNomeEscritorio] = useState(usuario?.nome_escritorio || "");
    const [salvando, setSalvando] = useState(false);
    const [enviandoLogo, setEnviandoLogo] = useState(false);
    const [erro, setErro] = useState(null);
    const [mensagem, setMensagem] = useState(null);
    const inputArquivoRef = useRef(null);

    // Feriados manuais do escritório (municipais / suspensões de expediente)
    const [feriados, setFeriados] = useState([]);
    const [novoFeriado, setNovoFeriado] = useState({ data: "", descricao: "" });


    useEffect(() => {
        setCor(usuario?.cor_primaria || "#b8862b");
        setNomeEscritorio(usuario?.nome_escritorio || "");
    }, [usuario]);

    useEffect(() => {
        api.get("/feriados").then((r) => setFeriados(r.data)).catch(() => {});
    }, []);

    const adicionarFeriado = async () => {
        if (!novoFeriado.data || !novoFeriado.descricao.trim()) {
            setErro("Informe a data e a descrição do feriado.");
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            await api.post("/feriados", novoFeriado);
            setNovoFeriado({ data: "", descricao: "" });
            const r = await api.get("/feriados");
            setFeriados(r.data);
            avisar("Feriado cadastrado.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível cadastrar o feriado.");
        } finally {
            setSalvando(false);
        }
    };

    const removerFeriado = async (cod_feriado) => {
        try {
            await api.delete(`/feriados/${cod_feriado}`);
            setFeriados((atual) => atual.filter((f) => f.cod_feriado !== cod_feriado));
            avisar("Feriado removido.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível remover o feriado.");
        }
    };

    const avisar = (texto) => {
        setMensagem(texto);
        setErro(null);
        setTimeout(() => setMensagem(null), 4000);
    };

    const mudarEscala = (valor) => {
        aplicarEscalaInterface(valor);
        setEscala(valor);
    };

    const mudarTema = (valor) => {
        aplicarTema(valor);
        setTema(valor);
        // O accent depende do tema (tom mais escuro no claro, mais claro no escuro)
        aplicarCorPrimaria(usuario?.cor_primaria);
    };

    const mudarDensidade = (valor) => { aplicarDensidade(valor); setDensidade(valor); };
    const mudarFonte = (valor) => { aplicarFonte(valor); setFonte(valor); };
    const mudarContraste = (valor) => { aplicarContraste(valor); setContraste(valor); };

    const salvarCor = async (novaCor) => {
        setCor(novaCor);
        aplicarCorPrimaria(novaCor); // preview imediato
        setSalvando(true);
        setErro(null);
        try {
            await api.patch("/escritorio", { cor_primaria: novaCor });
            await recarregarEscritorio();
            avisar("Cor de destaque atualizada.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível salvar a cor.");
            aplicarCorPrimaria(usuario?.cor_primaria);
        } finally {
            setSalvando(false);
        }
    };

    const salvarNome = async () => {
        setSalvando(true);
        setErro(null);
        try {
            await api.patch("/escritorio", { nome: nomeEscritorio });
            await recarregarEscritorio();
            avisar("Nome do escritório atualizado.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível salvar o nome.");
        } finally {
            setSalvando(false);
        }
    };

    const enviarLogo = async (evento) => {
        const arquivo = evento.target.files?.[0];
        if (!arquivo) return;
        setEnviandoLogo(true);
        setErro(null);
        try {
            const dados = new FormData();
            dados.append("arquivo", arquivo);
            await api.post("/escritorio/logo", dados, { headers: { "Content-Type": "multipart/form-data" } });
            await recarregarEscritorio();
            avisar("Logo atualizada.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível enviar a logo.");
        } finally {
            setEnviandoLogo(false);
            if (inputArquivoRef.current) inputArquivoRef.current.value = "";
        }
    };

    const removerLogo = async () => {
        if (!window.confirm("Voltar para a logo padrão do Selo Jurídico?")) return;
        try {
            await api.delete("/escritorio/logo");
            await recarregarEscritorio();
            avisar("Logo removida.");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível remover a logo.");
        }
    };

    return (
        <div>
            <Header />

            <main className="page">
                <h2 style={{ margin: 0 }}>Configurações</h2>

                {erro && <p className="erro">{erro}</p>}
                {mensagem && <p className="sucesso">{mensagem}</p>}

                {/* Preferências de quem está usando — todos os papéis */}
                <Card>
                    <h3 className="card-title">Tamanho do texto</h3>
                    <p className="card-body">Ajusta toda a interface — útil para ler com mais conforto.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {ESCALAS_DISPONIVEIS.map((opcao) => (
                            <Button
                                key={opcao.valor}
                                variante={escala === opcao.valor ? "primary" : "secondary"}
                                onClick={() => mudarEscala(opcao.valor)}
                            >
                                {opcao.rotulo}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="card-title">Tema</h3>
                    <p className="card-body">Claro (papel), escuro, ou seguir a preferência do seu computador.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {TEMAS_DISPONIVEIS.map((opcao) => (
                            <Button
                                key={opcao.valor}
                                variante={tema === opcao.valor ? "primary" : "secondary"}
                                onClick={() => mudarTema(opcao.valor)}
                            >
                                {opcao.rotulo}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="card-title">Fonte do texto</h3>
                    <p className="card-body">A serifada costuma parecer mais formal e ajuda na leitura de textos longos.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {FONTES_DISPONIVEIS.map((opcao) => (
                            <Button key={opcao.valor} variante={fonte === opcao.valor ? "primary" : "secondary"} onClick={() => mudarFonte(opcao.valor)}>
                                {opcao.rotulo}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="card-title">Contraste</h3>
                    <p className="card-body">O alto contraste escurece o texto e reforça as bordas — útil para baixa visão.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {CONTRASTES_DISPONIVEIS.map((opcao) => (
                            <Button key={opcao.valor} variante={contraste === opcao.valor ? "primary" : "secondary"} onClick={() => mudarContraste(opcao.valor)}>
                                {opcao.rotulo}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="card-title">Densidade das tabelas</h3>
                    <p className="card-body">O modo compacto mostra mais linhas por tela — útil com muitos registros.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {DENSIDADES_DISPONIVEIS.map((opcao) => (
                            <Button key={opcao.valor} variante={densidade === opcao.valor ? "primary" : "secondary"} onClick={() => mudarDensidade(opcao.valor)}>
                                {opcao.rotulo}
                            </Button>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="card-title">Cor de destaque</h3>
                    <p className="card-body">
                        Usada em botões, links e destaques.
                        {ehProprietario ? " Vale para todo o escritório." : " Só o proprietário pode alterar."}
                    </p>
                    <div className="amostras-cor" style={{ marginTop: 8 }}>
                        {CORES_SUGERIDAS.map((sugestao) => (
                            <button
                                key={sugestao}
                                type="button"
                                className="amostra-cor"
                                aria-label={`Usar a cor ${sugestao}`}
                                aria-pressed={cor.toLowerCase() === sugestao}
                                disabled={!ehProprietario || salvando}
                                onClick={() => salvarCor(sugestao)}
                                style={{ background: sugestao }}
                            />
                        ))}
                        {ehProprietario && (
                            <input
                                type="color"
                                value={cor}
                                onChange={(e) => setCor(e.target.value)}
                                onBlur={(e) => salvarCor(e.target.value)}
                                disabled={salvando}
                                aria-label="Cor personalizada"
                                style={{
                                    width: 44, height: 36, padding: 2, cursor: "pointer",
                                    border: "1px solid var(--color-divider)", borderRadius: "var(--radius-md)",
                                    background: "var(--color-bg)"
                                }}
                            />
                        )}
                    </div>
                </Card>

                {/* Identidade do escritório — só proprietário */}
                {ehProprietario && (
                    <>
                        <Card>
                            <h3 className="card-title">Logo do escritório</h3>
                            <p className="card-body">
                                PNG, JPG, WEBP ou SVG, até 2 MB. Aparece no cabeçalho do sistema.
                                Sem logo própria, usamos o selo do Selo Jurídico.
                            </p>
                            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
                                <div className="preview-logo">
                                    <img src={logoEscritorio} alt="Logo atual do escritório" />
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <input
                                        ref={inputArquivoRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                        onChange={enviarLogo}
                                        disabled={enviandoLogo}
                                        style={{ display: "none" }}
                                    />
                                    <Button
                                        variante="secondary"
                                        disabled={enviandoLogo}
                                        onClick={() => inputArquivoRef.current?.click()}
                                    >
                                        {enviandoLogo ? "Enviando..." : "Escolher arquivo"}
                                    </Button>
                                    {usuario?.logo_url && (
                                        <Button variante="danger" onClick={removerLogo}>Usar logo padrão</Button>
                                    )}
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <h3 className="card-title">Nome do escritório</h3>
                            <p className="card-body">Aparece no cabeçalho e na tela de login da sua equipe.</p>
                            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                                <Field label="Nome">
                                    <Input
                                        value={nomeEscritorio}
                                        onChange={(e) => setNomeEscritorio(e.target.value)}
                                        style={{ minWidth: 260 }}
                                    />
                                </Field>
                                <Button onClick={salvarNome} disabled={salvando || !nomeEscritorio.trim()}>
                                    Salvar
                                </Button>
                            </div>
                        </Card>

                        <Card>
                            <h3 className="card-title">Feriados e suspensões de expediente</h3>
                            <p className="card-body">
                                Os prazos processuais contam em <strong>dias úteis</strong> (CPC art. 219).
                                Feriados <strong>nacionais</strong> (incl. Carnaval, Sexta-Feira Santa e
                                Corpus Christi) e <strong>estaduais</strong> (pela UF do escritório) já entram
                                automaticamente. Cadastre aqui só o que a fonte automática não cobre:
                                feriados <strong>municipais</strong> e <strong>suspensões de expediente</strong> da
                                comarca (portarias do tribunal). Cada data cadastrada deixa de contar como dia útil.
                            </p>

                            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                                <Field label="Data">
                                    <Input
                                        type="date"
                                        value={novoFeriado.data}
                                        onChange={(e) => setNovoFeriado((f) => ({ ...f, data: e.target.value }))}
                                    />
                                </Field>
                                <Field label="Descrição">
                                    <Input
                                        placeholder="Ex.: Aniversário da cidade / Portaria nº 12"
                                        value={novoFeriado.descricao}
                                        onChange={(e) => setNovoFeriado((f) => ({ ...f, descricao: e.target.value }))}
                                        style={{ minWidth: 260 }}
                                    />
                                </Field>
                                <Button onClick={adicionarFeriado} disabled={salvando}>Adicionar</Button>
                            </div>

                            {feriados.length > 0 ? (
                                <ul className="mini-lista" style={{ marginTop: 12 }}>
                                    {feriados.map((f) => (
                                        <li key={f.cod_feriado}>
                                            <strong style={{ fontVariantNumeric: "tabular-nums" }}>{dataBR(f.data)}</strong>
                                            {" — "}{f.descricao}
                                            <Button
                                                variante="ghost"
                                                className="btn-icon"
                                                aria-label={`Remover feriado ${dataBR(f.data)}`}
                                                onClick={() => removerFeriado(f.cod_feriado)}
                                                style={{ marginLeft: "auto" }}
                                            >
                                                ✕
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted" style={{ marginTop: 12 }}>
                                    Nenhum feriado manual cadastrado. Os nacionais e estaduais continuam valendo.
                                </p>
                            )}
                        </Card>
                    </>
                )}

                <Card style={{ alignItems: "center", padding: "20px", gap: 4 }}>
                    <SeloSystems />
                    <p className="card-body text-muted" style={{ textAlign: "center", marginTop: 8 }}>
                        Selo Jurídico é um produto Selo Systems.
                    </p>
                </Card>
            </main>
        </div>
    );
};

export default Configuracoes;
